import { useEffect, useState } from 'react';
import pushService from '../services/pushService';
import { useAuth } from '../context/AuthContext';
import PromptSheet, {
  APP_ICON, EXIT_MS, usePromptClose, CloseButton, DragHandle, AppIconTile, PromptCopy, PromptActions,
} from './PromptSheet';

const SHOW_DELAY_MS = 45 * 1000;
// Cooldown state is owned by pushService now. EnablePushPrompt just consults
// pushService.inCooldown() (true if user-dismiss or recent failure) and uses
// the appropriate setter when the user takes an action here. Subscribe-side
// failures (no VAPID, network blip) get cooldowned by pushService itself with
// a SHORT timer, so a misconfigured backend doesn't permanently silence the
// re-prompt — next session the user will be asked again.

const HUMAN = 'Arial, Helvetica, sans-serif';
const KINDS = [
  ['INVITES', '#F5D3F1'], ['MESSAGES', '#A8D8EA'], ['TICKET SALES', '#F2F2F2'], ['REFUNDS', '#F2F2F2'], ['RSVPS', '#DCE3F8'],
];

/**
 * EnablePushPrompt — soft-asks an authed user to enable browser push.
 *
 * Why a soft prompt instead of just calling Notification.requestPermission()
 * on page load: if the user clicks "Block" on the native browser dialog the
 * permission becomes 'denied' permanently and we can't ask again from JS.
 * The soft prompt lets us re-ask later (14-day cooldown if dismissed) and
 * only escalates to the real browser dialog when the user opts in.
 *
 * Skips entirely if:
 *   - User is not signed in
 *   - Browser doesn't support Web Push
 *   - Permission is already 'granted' (we still re-subscribe quietly to keep
 *     the backend's subscription record fresh — handles user-switching)
 *   - Permission is already 'denied' (user said no; nothing JS can do)
 *   - In cooldown
 *
 * After 45s of authed engagement, a white sheet drops from the top with a
 * sample notification and one primary action: TURN ON NOTIFICATIONS. Click →
 * pushService.ensureSubscribed (Notification.requestPermission + backend
 * registration). Design: design_handoff_install_notify_icons §1c.
 */
export default function EnablePushPrompt() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [closing, close] = usePromptClose(EXIT_MS.down, () => setShow(false));

  useEffect(() => {
    if (!user) return;
    if (!pushService.isSupported()) return;

    let timer = null;

    let cancelled = false;
    (async () => {
    // Native reports through the OS, not the Web Notification API — see
    // pushService.nativePermission.
    const perm = await pushService.nativePermission();
    if (cancelled) return;
    if (perm === 'granted') {
      // Already approved — silently re-bind in case the user switched accounts
      // or the previous attempt failed mid-flight. pushService.ensureSubscribed
      // is idempotent: if a fresh sub already exists + the backend already has
      // it, it's a no-op POST. If the prior attempt failed because the backend
      // had no VAPID config, this is where we automatically recover once the
      // operator fixes it — no UI shown either way.
      pushService.ensureSubscribed().catch(() => {});
      return;
    }
    if (perm === 'denied') return;
    if (pushService.inCooldown()) return;

    timer = setTimeout(() => setShow(true), SHOW_DELAY_MS);
    })();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [user]);

  if (!show) return null;

  const dismiss = () => {
    if (closing) return;
    // User actively said "not now" — long cooldown.
    pushService.setDismissCooldown();
    close();
  };
  const turnOn = async () => {
    setBusy(true);
    await pushService.ensureSubscribed();
    setBusy(false);
    close();
    // Cooldown bookkeeping is owned by pushService:
    //   - reason 'denied'           → long dismiss-cooldown set inside ensureSubscribed
    //   - reason 'no_vapid' / 'subscribe_failed' / network → short failure-cooldown
    //   - success                    → both cooldowns cleared
    // So nothing to do here on failure — the next session re-prompts.
  };

  const bell = (
    <span style={{ position: 'absolute', right: -8, bottom: -8, width: 36, height: 36, borderRadius: '50%', background: '#000', border: '3px solid #fff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="square" strokeLinejoin="miter">
        <path d="M6 16V10a6 6 0 0 1 12 0v6l2 2H4z" /><path d="M10 21h4" />
      </svg>
    </span>
  );

  return (
    <PromptSheet direction="down" closing={closing} onDismiss={dismiss} label="Turn on notifications">
      <CloseButton onClick={dismiss} style={{ top: 'calc(52px + env(safe-area-inset-top, 0px))', right: 16 }} />
      <div className="pr-prompt-rise" style={{ padding: '28px 24px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 18 }}>
        <AppIconTile badge={bell} />
        <PromptCopy
          eyebrow="STAY IN THE LOOP"
          title="GET NOTIFIED WHEN SOMETHING HAPPENS"
          body="Invites, messages, ticket sales, refunds, RSVPs. Push them to your phone in real time."
        />
      </div>

      <div style={{ margin: '22px 24px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div data-testid="push-sample" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', background: '#F2F2F2', borderRadius: 16, textAlign: 'left' }}>
          <img src={APP_ICON} alt="" style={{ width: 36, height: 36, borderRadius: 9, background: '#0E0A22', display: 'block', flexShrink: 0 }} />
          <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em' }}>PORTAL</span>
              <span style={{ fontSize: 10, letterSpacing: '.08em', color: '#6B7280' }}>NOW</span>
            </div>
            <span style={{ fontFamily: HUMAN, fontSize: 13, lineHeight: 1.4, color: '#1A1A1A' }}>
              Maya invited you to Wine + Vinyl, Saturday at the Bench.
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 6 }}>
          {KINDS.map(([label, bg]) => (
            <span key={label} style={{ padding: '5px 10px', borderRadius: 999, background: bg, fontSize: 10, fontWeight: 700, letterSpacing: '.14em' }}>{label}</span>
          ))}
        </div>
      </div>

      <PromptActions
        primaryLabel={busy ? 'TURNING ON…' : 'TURN ON NOTIFICATIONS'}
        primaryDisabled={busy}
        onPrimary={turnOn}
        onSecondary={dismiss}
      />
      <DragHandle style={{ padding: '0 0 10px', marginTop: -18 }} />
    </PromptSheet>
  );
}
